
import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useToast } from "@/hooks/use-toast";
import { getUserLocationFromIP } from "@/utils/geo-location";
import { locationSchema, LocationFormValues } from "./types";
import { Profile } from "@/hooks/auth/profile/types";
import { useUserProfile } from "@/hooks/use-user-profile";

export const useLocationForm = (profile: Profile | null, onUpdate: () => Promise<boolean>) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDetecting, setIsDetecting] = useState(false);
  const { toast } = useToast();
  const { updateLocationData } = useUserProfile();

  // Initialize form with default values
  const form = useForm<LocationFormValues>({
    resolver: zodResolver(locationSchema),
    defaultValues: {
      country: profile?.country || "",
      region: profile?.region || "",
      city: profile?.city || "",
    },
    mode: "onBlur", // Validate on blur for better UX
  });

  const onSubmit = async (values: LocationFormValues) => {
    if (!profile?.id) return;
    
    setIsSubmitting(true);
    try {
      const success = await updateLocationData(
        values.country, 
        values.region, 
        values.city
      );

      if (!success) {
        throw new Error("Failed to update location");
      }

      await onUpdate();
      
      toast({
        title: "Location updated",
        description: "Your location information has been updated successfully.",
      });
    } catch (error) {
      console.error("Error updating location:", error);
      toast({
        title: "Error",
        description: "There was a problem updating your location.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const detectLocation = async () => {
    setIsDetecting(true);
    try {
      const locationData = await getUserLocationFromIP();
      
      if (locationData.country) {
        form.setValue("country", locationData.country);
        
        if (locationData.region) {
          form.setValue("region", locationData.region);
        }
        
        if (locationData.city) {
          form.setValue("city", locationData.city);
        }
        
        toast({
          title: "Location detected",
          description: `We've detected your location as ${locationData.city ? locationData.city + ', ' : ''}${locationData.region ? locationData.region + ', ' : ''}${locationData.country}`,
        });
      } else {
        toast({
          title: "Location detection failed",
          description: "We couldn't automatically detect your location. Please enter it manually.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error detecting location:", error);
      toast({
        title: "Error",
        description: "There was a problem detecting your location.",
        variant: "destructive",
      });
    } finally {
      setIsDetecting(false);
    }
  };

  return {
    form,
    isSubmitting,
    isDetecting,
    onSubmit,
    detectLocation
  };
};
